/*
 * @Author: Ryan
 * @Date: 2026-02-04
 * @Description: Hero Supporting Section - What TikTok Videos Can FalcoCut Make (3 tool cards)
 */
import Image from 'next/image';
import Link from 'next/link';
import React from 'react';

interface ToolCard {
  id: string;
  title: string;
  description: string;
  imageUrl: string;
}

const tools: ToolCard[] = [
  {
    id: 'image-to-video',
    title: 'Image to Video',
    description:
      'Turn any product link or webpage into a TikTok video in minutes. Perfect for e-commerce, affiliate marketing, and quick product promos.',
    imageUrl:
      'https://assets.falcocut.ai/tools/tiktok-video-generator/image-to-video.webp',
  },
  {
    id: 'text-to-video',
    title: 'Text to Video',
    description:
      'Start with a script or a simple idea. AI turns your text into a complete TikTok video with scenes, visuals, and pacing optimized.',
    imageUrl:
      'https://assets.falcocut.ai/tools/tiktok-video-generator/text-to-video.webp',
  },
  {
    id: 'ai-avatars',
    title: 'AI Avatars',
    description:
      'Choose from a large library of realistic avatars or create your own custom avatar to match your brand.',
    imageUrl: 'https://assets.falcocut.ai/tools/tiktok-video-generator/ai-avatars.webp',
  },
];

const HeroSupportingSection: React.FC = () => {
  return (
    <section className="w-full bg-black py-[60px] pad:py-[70px] sm:py-[80px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* Header */}
        <div className="text-center mb-8 pad:mb-10 sm:mb-12">
          <h2 className="text-2xl pad:text-3xl sm:text-4xl font-bold text-white mb-4 leading-tight">
            What TikTok Videos Can FalcoCut Make
          </h2>
          <p className="text-sm pad:text-base text-white/65 max-w-[900px] mx-auto leading-relaxed">
            Simplify TikTok video creation, scale your content production, and create
            high-performing short videos faster. Use powerful AI tools and realistic
            avatars to turn ideas, links, and assets into TikTok-ready videos — without
            complex editing.
          </p>
        </div>

        {/* Tool Cards - 3 columns */}
        <div className="flex flex-col sm:flex-row gap-6 pad:gap-5 sm:gap-6 justify-center items-center sm:items-stretch mb-8 pad:mb-10 sm:mb-12">
          {tools.map((tool) => (
            <div
              key={tool.id}
              className="w-full max-w-[340px] sm:max-w-none sm:flex-1 flex flex-col"
            >
              {/* Image */}
              <div className="relative w-full aspect-square rounded-2xl pad:rounded-xl overflow-hidden bg-[#151519] transition-transform duration-300 hover:-translate-y-1 hover:shadow-lg sm:hover:-translate-y-1 sm:hover:shadow-lg">
                <Image
                  src={tool.imageUrl}
                  alt={tool.title}
                  width={340}
                  height={340}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              {/* Text */}
              <div className="pt-4 pad:pt-4 sm:pt-5 text-center sm:text-left">
                <h3 className="text-base pad:text-lg sm:text-xl font-semibold text-white mb-1.5 pad:mb-2">
                  {tool.title}
                </h3>
                <p className="text-xs pad:text-sm sm:text-sm text-white/50 leading-relaxed">
                  {tool.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Button */}
        <div className="flex justify-center">
          <Link
            href="/ai-video-generator"
            className="inline-flex items-center justify-center h-12 pad:h-[42px] px-8 pad:px-7 text-base pad:text-[15px] font-medium text-white rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-lg"
            style={{
              background: 'linear-gradient(135deg, #7c3aed 0%, #ec4899 100%)',
            }}
          >
            Create TikTok Videos with AI
          </Link>
        </div>
      </div>
    </section>
  );
};

export default HeroSupportingSection;
